import { z } from 'zod';

export const approvalStatusSchema = z.union([
  z.literal('pending'),
  z.literal('approved'),
  z.literal('rejected'),
  z.literal('edited'),
  z.literal('auto_approved'),
  z.literal('expired'),
]);
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

export const executionStatusSchema = z.union([
  z.literal('pending'),
  z.literal('in_progress'),
  z.literal('completed'),
  z.literal('partial_failure'),
  z.literal('failed'),
]);

export const rejectionReasonTagSchema = z.union([
  z.literal('needs_clarifying_question'),
  z.literal('wrong_facts'),
  z.literal('wrong_policy'),
  z.literal('wrong_order_context'),
  z.literal('tone_too_generic'),
  z.literal('duplicate_response'),
  z.literal('premature_escalation'),
  z.literal('action_not_supported'),
  z.literal('other'),
]);
export type RejectionReasonTag = z.infer<typeof rejectionReasonTagSchema>;

export const REJECTION_REASON_TAGS: readonly RejectionReasonTag[] = [
  'needs_clarifying_question',
  'wrong_facts',
  'wrong_policy',
  'wrong_order_context',
  'tone_too_generic',
  'duplicate_response',
  'premature_escalation',
  'action_not_supported',
  'other',
] as const;

export const REJECTION_REASON_TAG_LABELS: Record<RejectionReasonTag, string> = {
  needs_clarifying_question: 'Needs clarifying question',
  wrong_facts: 'Wrong facts',
  wrong_policy: 'Wrong policy',
  wrong_order_context: 'Wrong order context',
  tone_too_generic: 'Tone too generic',
  duplicate_response: 'Duplicate response',
  premature_escalation: 'Premature escalation',
  action_not_supported: 'Action not supported',
  other: 'Other',
};

export const proposedActionSchema = z.object({
  type: z.string(),
  action_id: z.string(),
  description: z.string(),
  is_financial: z.boolean(),
  payload: z.record(z.unknown()),
  permissions: z.array(z.string()),
});
export type ProposedAction = z.infer<typeof proposedActionSchema>;

export const agentApprovalSchema = z.object({
  id: z.string(),
  chat_id: z.string(),
  request_id: z.string(),
  task_id: z.string(),
  proposed_message: z.string(),
  sent_message: z.string().optional(),
  internal: z.boolean().default(false),
  proposed_actions: z.array(proposedActionSchema).default([]),
  approved_action_ids: z.array(z.string()).default([]),
  status: approvalStatusSchema,
  reviewed_by: z.string().optional(),
  rejection_reason: z.string().optional(),
  reason_tags: z.array(rejectionReasonTagSchema).optional(),
  execution_status: executionStatusSchema.optional(),
  execution_errors: z
    .array(
      z.object({
        action_id: z.string(),
        error: z.string(),
        timestamp: z.number(),
      })
    )
    .default([]),
  context_snapshot: z.record(z.unknown()).optional(),
  created: z.number(),
  reviewed_at: z.number().optional(),
  sent_at: z.number().optional(),
  expires_at: z.number().optional(),
});
export type AgentApproval = z.infer<typeof agentApprovalSchema>;

export const agentMetricsSchema = z.object({
  pending_count: z.number().default(0),
  approved_count: z.number().default(0),
  rejected_count: z.number().default(0),
  edited_count: z.number().default(0),
  auto_approved_count: z.number().default(0),
  last_updated: z.number().optional(),
});
export type AgentMetrics = z.infer<typeof agentMetricsSchema>;
